import {container, injectable} from "tsyringe";
import {ChangeTracker} from "../change-tracking/change-tracker";
import {transformer} from "../decorators/transformer.decorator";
import {WeeklyWorkingTimeDto} from "../domain/dtos/weekly-working-time.dto";
import {Employee} from "../domain/entities/employee.entity";
import {WeeklyWorkingTime} from "../domain/entities/weekly-working-time.entity";
import {EmployeeRepository} from "../domain/repositories/employee.repository";
import {AbstractDtoToEntityTransformer} from "./abstract-dto-to-entity.transformer";

/**
 * Transforms WeeklyWorkingTimeDto from backend to WeeklyWorkingTime entity.
 * Required for Employee loading to resolve weeklyWorkingTime references.
 */
@injectable()
@transformer({
    source: `dto`,
    target: WeeklyWorkingTime
})
export class DtoToWeeklyWorkingTimeTransformer extends AbstractDtoToEntityTransformer<WeeklyWorkingTimeDto, WeeklyWorkingTime> {

    protected toEntity(dto: WeeklyWorkingTimeDto): Promise<WeeklyWorkingTime> {
        const weeklyWorkingTime = Object.assign(new WeeklyWorkingTime(), {
            monday: dto.monday ?? 8,
            tuesday: dto.tuesday ?? 8,
            wednesday: dto.wednesday ?? 8,
            thursday: dto.thursday ?? 8,
            friday: dto.friday ?? 8,
            saturday: dto.saturday ?? 0,
            sunday: dto.sunday ?? 0
        });

        // The employee back-reference has to be carried over: Create/UpdateWeeklyWorkingTimeCommand
        // both read `payload.employee.uid` in their constructor, so without it saving the weekly
        // working time of an EXISTING employee throws "Cannot read properties of undefined". Only
        // the create path (WeeklyWorkingTimeRepository.create) used to set it, which is why the
        // crash was limited to employees whose working time was already stored.
        //
        // Resolved as a promise carrying its own uid, the pattern EmployeeToIssueAssignment uses
        // for its own references: the dto delivers a uid string, and eagerly awaiting the Employee
        // here would re-enter DtoToEmployeeTransformer while that transform is still in flight.
        // Both readers only need `.uid`, and they need it synchronously.
        if (dto.employee) {
            const employee = container.resolve(EmployeeRepository).findOneById(dto.employee);
            Reflect.set(employee, `uid`, dto.employee);
            weeklyWorkingTime.employee = employee as Promise<Employee> & { uid: string };
        }

        // Wrapped LAST, and deliberately so. The command dispatch rejects a payload that is not
        // change-tracked (TransformerHelper's anonymous fallback: "The payload of the command must
        // be a proxified object"), so the loaded entity has to be wrapped the same way the create
        // path wraps it.
        //
        // The back-reference is assigned BEFORE this line on purpose: an update payload is built
        // from changeTracker.getChangedValues(), so a value assigned after wrapping would count as
        // a change and be serialized. WeeklyWorkingTime.employee has no to-uid transform, so that
        // would put a whole Employee object graph on the wire — a shape the backend has never
        // received. Assigned before the wrap it is part of the baseline and never sent.
        return Promise.resolve(ChangeTracker.create(weeklyWorkingTime));
    }
}
